
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from './Navbar';

import { Mail, MessageSquare, Send, User, AtSign, FileText, ChevronRight, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";

const Contact = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        subject: '',
        message: ''
    });
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            await axios.post(`${API_URL}/api/contact`, formData);
            setSubmitted(true);
            toast.success("Message sent successfully!");
        } catch (error) {
            console.error(error);
            toast.error("Failed to send message. Please try again or email us directly.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#050505] flex flex-col">
            <Navbar />

            <div className="flex-1 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20 flex flex-col md:flex-row gap-16 items-start">

                {/* Left Side: Contact Info */}
                <div className="w-full md:w-1/3 space-y-8">
                    <div>
                        <h1 className="text-4xl md:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-pink-400 mb-6 font-heading leading-tight">
                            Let's Talk
                        </h1>
                        <p className="text-gray-400 text-lg leading-relaxed">
                            Have a question, feedback, or just want to say hi? We'd love to hear from you.
                            Our team is always ready to help creators and readers alike.
                        </p>
                    </div>

                    <div className="bg-gradient-to-br from-gray-900 to-black p-8 rounded-3xl border border-white/5 shadow-2xl">
                        <h3 className="text-white font-semibold text-xl mb-6 flex items-center">
                            <Mail className="w-5 h-5 mr-3 text-purple-400" />
                            Direct Contact
                        </h3>
                        <p className="text-gray-500 text-sm mb-2 uppercase tracking-wider font-bold">Email Us</p>
                        <a
                            href="mailto:comicmenia@gmail.com"
                            className="text-2xl text-white font-medium hover:text-purple-400 transition-colors break-all"
                        >
                            comicmenia@gmail.com
                        </a>

                        <div className="mt-8 pt-8 border-t border-white/5">
                            <p className="text-gray-400 text-sm">
                                We typically respond within 24-48 hours.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Right Side: Form */}
                <div className="w-full md:w-2/3 bg-[#0a0a0a] rounded-3xl p-8 md:p-12 border border-white/5 shadow-2xl relative overflow-hidden group">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-purple-600/5 rounded-full blur-3xl -mr-32 -mt-32 pointer-events-none group-hover:bg-purple-600/10 transition-colors" />

                    {submitted ? (
                        <div className="h-full min-h-[400px] flex flex-col items-center justify-center text-center animate-in fade-in zoom-in duration-500">
                            <div className="w-20 h-20 rounded-full bg-green-500/10 flex items-center justify-center mb-6">
                                <CheckCircle className="w-10 h-10 text-green-400" />
                            </div>
                            <h2 className="text-3xl font-bold text-white mb-4">Message Sent!</h2>
                            <p className="text-gray-400 max-w-md mx-auto mb-8">
                                Thank you for reaching out. We have received your message and will get back to you shortly at <span className="text-white">{formData.email}</span>.
                            </p>
                            <button
                                onClick={() => {
                                    setSubmitted(false);
                                    setFormData({ name: '', email: '', subject: '', message: '' });
                                }}
                                className="px-8 py-3 rounded-xl bg-white/5 hover:bg-white/10 text-white font-medium transition-all"
                            >
                                Send Another Message
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-6 relative z-10">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-gray-400 ml-1">Name</label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                            <User className="w-5 h-5 text-gray-500" />
                                        </div>
                                        <input
                                            type="text"
                                            name="name"
                                            required
                                            value={formData.name}
                                            onChange={handleChange}
                                            className="w-full pl-12 pr-4 py-4 bg-black/40 border border-white/10 rounded-xl text-white placeholder:text-gray-600 focus:border-purple-500/50 focus:ring-1 focus:ring-purple-500/50 transition-all outline-none"
                                            placeholder="Your name"
                                        />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-gray-400 ml-1">Email</label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                            <AtSign className="w-5 h-5 text-gray-500" />
                                        </div>
                                        <input
                                            type="email"
                                            name="email"
                                            required
                                            value={formData.email}
                                            onChange={handleChange}
                                            className="w-full pl-12 pr-4 py-4 bg-black/40 border border-white/10 rounded-xl text-white placeholder:text-gray-600 focus:border-purple-500/50 focus:ring-1 focus:ring-purple-500/50 transition-all outline-none"
                                            placeholder="you@company.com"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-400 ml-1">Subject</label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                        <FileText className="w-5 h-5 text-gray-500" />
                                    </div>
                                    <input
                                        type="text"
                                        name="subject"
                                        required
                                        value={formData.subject}
                                        onChange={handleChange}
                                        className="w-full pl-12 pr-4 py-4 bg-black/40 border border-white/10 rounded-xl text-white placeholder:text-gray-600 focus:border-purple-500/50 focus:ring-1 focus:ring-purple-500/50 transition-all outline-none"
                                        placeholder="What is this regarding?"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-400 ml-1">Message</label>
                                <div className="relative">
                                    <div className="absolute top-4 left-4 pointer-events-none">
                                        <MessageSquare className="w-5 h-5 text-gray-500" />
                                    </div>
                                    <textarea
                                        name="message"
                                        required
                                        rows={6}
                                        value={formData.message}
                                        onChange={handleChange}
                                        className="w-full pl-12 pr-4 py-4 bg-black/40 border border-white/10 rounded-xl text-white placeholder:text-gray-600 focus:border-purple-500/50 focus:ring-1 focus:ring-purple-500/50 transition-all outline-none resize-none"
                                        placeholder="Tell us more..."
                                    />
                                </div>
                            </div>

                            <div className="pt-4">
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="w-full py-4 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-bold rounded-xl shadow-lg shadow-purple-900/20 hover:shadow-purple-900/40 hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 className="w-5 h-5 animate-spin" />
                                            <span>Sending...</span>
                                        </>
                                    ) : (
                                        <>
                                            <span>Send Message</span>
                                            <Send className="w-5 h-5" />
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>


        </div>
    );
};

export default Contact;
